import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';

import {
  INestedElement,
  PkpMapComponent,
  PkpSignSegmentsLayerService,
} from '@shared/modules/pkp-map';
import { Breakpoints } from '@pkp/core';
import { NavigationPanelComponent } from './desktop/main/components';
import { MourningBannerComponent } from './shared/mourning-banner';
import { SIGN_SEGMENT_FROM_MAP_STATE } from './shared/sign-segment-page/sign-segment-page.constants';
import { QuickLoginLockComponent } from './shared/quick-login/quick-login-lock.component';
import { MobileAppDownloadBannerComponent } from './mobile/main/app-download-banner/app-download-banner.component';
import { BREAKPOINT_ROUTE_TOKEN } from '@pkp/tokens';
import { BREAKPOINT_REDIRECT_TOKEN } from '@pkp/tokens';
import { hidePreloader, LocalizationType, WebviewCommunicationService } from '@shared/core';
import { LocalizationService } from '@shared/modules/localization';
import { MessagesService } from '@shared/modules/messages';
import { UIModalService } from '@shared/ui';
import { QuickLoginService } from '@pkp/core';
import { registerLogoTap } from './shared/utils/logo-taps';

const HEADLIGHTS_BLINK_MS = 1200;

const NAV_INDICATOR_DELAY_MS = 200;
const DESKTOP_ACCOUNT_PAGES = [
  'profile',
  'settings',
  'feedback',
  'faq',
  'balance-history',
  'employees',
  'replenishment-requests',
  'sessions',
  'vehicles',
  'payment-cards',
  'subscriptions',
];

@Component({
  selector: 'pkp-root',
  imports: [
    RouterModule,
    PkpMapComponent,
    AsyncPipe,
    NavigationPanelComponent,
    MourningBannerComponent,
    QuickLoginLockComponent,
    MobileAppDownloadBannerComponent,
  ],
  styles: [
    `
      .nav-progress {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        z-index: 2000;
        background: rgba(76, 175, 80, 0.2);
        overflow: hidden;
        pointer-events: none;
      }
      .nav-progress::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40%;
        background: var(--basic-color, #4caf50);
        animation: pkp-nav-progress 1s ease-in-out infinite;
      }
      @keyframes pkp-nav-progress {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(350%);
        }
      }

      .header__logo {
        pointer-events: none;
      }
      .header__logo a {
        pointer-events: auto;
      }
      .header__logo img {
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.35));
      }
      .header__headlight {
        position: absolute;
        top: 75%;
        width: 7%;
        aspect-ratio: 1.4;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        background: radial-gradient(circle, #fff 0%, #fff3b0 40%, rgba(255, 230, 120, 0) 75%);
        box-shadow: 0 0 6px 2px rgba(255, 230, 120, 0.8);
        opacity: 0;
        pointer-events: none;
      }
      .header__headlight--left {
        left: 80.3%;
      }
      .header__headlight--right {
        left: 89.3%;
      }
      .header__logo--blink .header__headlight {
        animation: pkp-headlights 0.4s ease-in-out 3;
      }
      @keyframes pkp-headlights {
        0%,
        100% {
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
      }
    `,
  ],
  template: `@if (quickLogin.lockState() !== 'hidden') { @defer {
    <pkp-quick-login-lock></pkp-quick-login-lock>
    } @placeholder {
    <div class="fixed inset-0 z-[1100] bg-white"></div>
    } } @if (isMourning) {
    <pkp-mourning-banner></pkp-mourning-banner>
    }
    <div class="pkp-app-shell">
      @if (isNavigating()) {
      <div class="nav-progress" aria-hidden="true"></div>
      } @if(isStandalonePage()){
      <router-outlet></router-outlet>
      } @else { @if((breakpointRouteToken | async) === breakpoints.Mobile){
      <pkp-map
        [showControls]="isMapScreen()"
        (listenToEventsOutput)="listenToEvents($event)"
      ></pkp-map>
      <div class="h-full">
        @if (!isParkingOrder() && !isFullSheet()) {
        <div
          class="top-[10px] absolute z-10 px-5 w-full header__logo"
          [class.header__logo--blink]="headlightsOn()"
        >
          <a
            [routerLink]="(breakpointRouteToken | async) + '/'"
            class="block relative"
            style="width: 120px"
            (click)="onLogoTap()"
          >
            <img src="assets/logos/logo-codd.png" alt="ЦОДД Парковки" />
            <span class="header__headlight header__headlight--left" aria-hidden="true"></span>
            <span class="header__headlight header__headlight--right" aria-hidden="true"></span>
          </a>
        </div>
        } @if (isMobileHome()) {
        <pkp-m-app-download-banner></pkp-m-app-download-banner>
        }
        <router-outlet></router-outlet>
      </div>
      } @if((breakpointRouteToken | async) === breakpoints.Desktop){ @if (isDesktopPage()) {
      <div class="fixed inset-0 z-[9] bg-black/40" aria-hidden="true"></div>
      }
      <div class="relative z-10 desktop-overlay">
        <pkp-d-navigation-panel></pkp-d-navigation-panel>
        <div
          class="desktop-content w-full max-w-[min(1610px,calc(100vw-160px))] max-h-[calc(100vh-136px)] my-7 mx-auto p-4"
        >
          <router-outlet></router-outlet>
        </div>
      </div>
      <pkp-map
        [showControls]="isMapScreen()"
        [desktopLayout]="true"
        (listenToEventsOutput)="listenToEvents($event)"
      ></pkp-map>
      } }
    </div>`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  protected readonly breakpointTypeToken = inject(BREAKPOINT_REDIRECT_TOKEN);
  protected readonly breakpointRouteToken = inject(BREAKPOINT_ROUTE_TOKEN);
  private readonly destroy = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly modalService = inject(UIModalService);
  private readonly webviewService = inject(WebviewCommunicationService);
  private readonly localizationService = inject(LocalizationService);
  private readonly messagesService = inject(MessagesService);
  private readonly translateService = inject(TranslateService);
  private readonly signSegmentsLayer = inject(PkpSignSegmentsLayerService);
  protected readonly quickLogin = inject(QuickLoginService);
  private langFromNs?: LocalizationType;

  breakpoints = Breakpoints;

  protected readonly isMourning = document.documentElement.classList.contains('pkp-mourning');

  protected readonly isStandalonePage = signal(this.isStandalone(window.location.pathname));

  protected readonly isMapScreen = signal(this.isMainScreen(window.location.pathname));

  protected readonly isDesktopPage = signal(this.isDesktopAccountPage(window.location.pathname));

  protected readonly isParkingOrder = signal(this.isParkingOrderPage(window.location.pathname));

  protected readonly isMobileHome = signal(this.isMobileHomePage(window.location.pathname));

  protected readonly isFullSheet = signal(this.isFullSheetPage(window.location.pathname));

  protected readonly isNavigating = signal(false);
  private navIndicatorTimer?: ReturnType<typeof setTimeout>;

  protected readonly headlightsOn = signal(false);
  private logoTaps: number[] = [];

  constructor() {
    effect(() => {
      const uuid = this.signSegmentsLayer.selectedId();
      if (uuid) {
        untracked(() => this.openSignSegment(uuid));
      }
    });
  }

  ngOnInit(): void {
    // заставка уходит, когда первый экран готов (первая навигация закончилась), а не по таймеру поверх него
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        take(1),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe(() => hidePreloader());

    this.showEmailConfirmResult();

    this.trackNavigationProgress();

    this.syncDocumentLanguage();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe(event => {
        this.isStandalonePage.set(this.isStandalone(event.urlAfterRedirects));
        this.isMapScreen.set(this.isMainScreen(event.urlAfterRedirects));
        this.isDesktopPage.set(this.isDesktopAccountPage(event.urlAfterRedirects));
        this.isParkingOrder.set(this.isParkingOrderPage(event.urlAfterRedirects));
        this.isMobileHome.set(this.isMobileHomePage(event.urlAfterRedirects));
        this.isFullSheet.set(this.isFullSheetPage(event.urlAfterRedirects));
      });

    this.breakpointTypeToken.pipe(takeUntilDestroyed(this.destroy)).subscribe(route => {
      const currentFullPath = window.location.pathname + window.location.search;

      if (this.isStandalone(currentFullPath)) {
        return;
      }

      const breakpoint = route.split('/')[0];
      if (currentFullPath.startsWith(`/${breakpoint}/`)) {
        return;
      }

      this.modalService.closeAll();

      if (currentFullPath.includes('/auth/recovery')) {
        const newPath = currentFullPath.replace(/^\/(mobile|desktop)/, `/${breakpoint}`);
        this.router.navigateByUrl(newPath).then();
      } else {
        this.router.navigateByUrl(`/${route}${window.location.search}`).then();
      }
    });

    this.webviewService.isNsWebViewInterfaceInitialized$.subscribe(isInit => {
      if (!isInit) {
        return;
      }
      this.webviewService.emit('isWebviewInterfaceInitialized', true);
      this.quickLogin.init();

      this.webviewService.on('selectedLanguage', (lang: LocalizationType) => {
        this.localizationService
          .setCurrentLang(lang)
          .pipe(takeUntilDestroyed(this.destroy))
          .subscribe(() => {
            this.langFromNs = lang;
            hidePreloader();
          });
      });

      this.localizationService.onLangChanged.subscribe(lang => {
        if (this.langFromNs === lang) {
          return;
        }
        this.webviewService.emit('selectedLanguage', lang);
      });
    });
  }

  /** Пасхалка: несколько быстрых тапов по логотипу — машинка мигает фарами. */
  protected onLogoTap(): void {
    if (this.headlightsOn()) {
      return;
    }
    const { taps, blink } = registerLogoTap(this.logoTaps, Date.now());
    this.logoTaps = taps;
    if (blink) {
      this.headlightsOn.set(true);
      setTimeout(() => this.headlightsOn.set(false), HEADLIGHTS_BLINK_MS);
    }
  }

  /** `<html lang>` и заголовок вкладки следуют выбранному языку (KZ → ISO 639-1 `kk`). */
  private syncDocumentLanguage(): void {
    this.translateService
      .stream('APP_DOCUMENT_TITLE')
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((title: string) => {
        const lang = this.localizationService.currentLanguage;
        document.documentElement.lang = lang === LocalizationType.KZ ? 'kk' : lang;
        document.title = title;
      });
  }

  private showEmailConfirmResult(): void {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('email_confirm');

    if (!result) {
      return;
    }

    const isSuccess = result === 'success';
    const key = isSuccess ? 'PROFILE_EMAIL_CONFIRMED' : 'PROFILE_EMAIL_CONFIRM_ERROR';

    this.translateService
      .get(key)
      .pipe(take(1), takeUntilDestroyed(this.destroy))
      .subscribe(text => {
        if (isSuccess) {
          this.messagesService.showSuccess(text);
        } else {
          this.messagesService.showError(text);
        }
      });

    params.delete('email_confirm');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  }

  private trackNavigationProgress(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroy)).subscribe(event => {
      if (event instanceof NavigationStart) {
        clearTimeout(this.navIndicatorTimer);
        this.navIndicatorTimer = setTimeout(
          () => this.isNavigating.set(true),
          NAV_INDICATOR_DELAY_MS,
        );
        return;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        clearTimeout(this.navIndicatorTimer);
        this.isNavigating.set(false);
      }
    });
  }

  private isStandalone(url: string): boolean {
    return url.startsWith('/privacy-policy') || url.startsWith('/support');
  }

  private isMainScreen(url: string): boolean {
    return url.startsWith('/mobile/main') || url.startsWith('/desktop/main');
  }

  // Страницы кабинета поверх карты: карту под ними затемняем.
  private isDesktopAccountPage(url: string): boolean {
    return DESKTOP_ACCOUNT_PAGES.some(page => url.startsWith(`/desktop/main/${page}`));
  }

  private isParkingOrderPage(url: string): boolean {
    return url.startsWith('/mobile/main/parking-order/');
  }

  private isFullSheetPage(url: string): boolean {
    return url.split(/[?#]/)[0].replace(/\/$/, '') === '/mobile/main/sessions';
  }

  private isMobileHomePage(url: string): boolean {
    return url.split(/[?#]/)[0].replace(/\/$/, '') === '/mobile/main';
  }

  private openSignSegment(uuid: string): void {
    const path = `/${this.breakpointRouteToken.getValue()}/main/sign-segment/${uuid}`;
    if (this.router.url.split('?')[0] === path) {
      return;
    }
    this.router.navigateByUrl(path, { state: { [SIGN_SEGMENT_FROM_MAP_STATE]: true } }).then();
  }

  listenToEvents(parking: INestedElement<string, string>): void {
    this.router
      .navigate([`${this.breakpointRouteToken.getValue()}/main/parking-details`, parking.id])
      .then();
  }
}
